import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Check } from "lucide-react";

const QUESTIONS = [
  {
    id: "q1", label: "Which best describes your current skin?",
    options: [
      { v: "dry",   l: "Dry & thirsty",              tags: ["face", "cleanser"] },
      { v: "oily",  l: "Oily / breakout-prone",      tags: ["cleanser", "sun"] },
      { v: "dull",  l: "Dull, needs radiance",       tags: ["face"] },
      { v: "combo", l: "Combination / normal",       tags: ["face", "sun"] },
    ],
  },
  {
    id: "q2", label: "What's your #1 concern?",
    options: [
      { v: "sun",   l: "Sun & pollution damage",     tags: ["sun"] },
      { v: "aging", l: "Fine lines & firmness",      tags: ["face"] },
      { v: "hair",  l: "Hair fall or thinning",      tags: ["hair"] },
      { v: "acne",  l: "Congestion & texture",       tags: ["cleanser"] },
    ],
  },
  {
    id: "q3", label: "How much time do you have each morning?",
    options: [
      { v: "1min",  l: "Under 1 minute",             tags: ["sun"] },
      { v: "3min",  l: "2–3 minutes",                tags: ["cleanser", "sun"] },
      { v: "5min",  l: "The full routine, please",    tags: ["face", "sun", "cleanser"] },
      { v: "hair",  l: "It's my hair that needs it", tags: ["hair"] },
    ],
  },
  {
    id: "q4", label: "Sunscreen every day?",
    options: [
      { v: "y", l: "Yes, always",                    tags: ["sun"] },
      { v: "s", l: "Sometimes",                      tags: ["sun"] },
      { v: "n", l: "Not really",                     tags: ["sun", "face"] },
    ],
  },
];

export default function SkinQuiz({ products = [] }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const pick = (opt) => {
    const next = { ...answers, [QUESTIONS[step].id]: opt };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) setStep(step + 1);
    else finish(next);
  };

  const finish = (final) => {
    const score = { face: 0, hair: 0, sun: 0, cleanser: 0 };
    Object.values(final).forEach((opt) => {
      opt.tags.forEach((t) => { score[t] = (score[t] || 0) + 1; });
    });
    const ranked = Object.entries(score).sort((a, b) => b[1] - a[1]).map(([k]) => k);
    const rec = ranked
      .map((cat) => products.find((p) => p.category === cat))
      .filter(Boolean)
      .slice(0, 3);
    setResult(rec);
  };

  const restart = () => { setStep(0); setAnswers({}); setResult(null); setOpen(false); };

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24" id="quiz">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="eyebrow flex items-center gap-2"><Sparkles size={12} className="text-gold-500" /> One-minute quiz</div>
          <h2 className="mt-3 font-display text-5xl leading-[1.05] text-ink-900">
            Find your <em className="serif-accent text-gold-500">routine</em> in 60 seconds.
          </h2>
          <p className="mt-6 text-ink-700 leading-relaxed">
            Answer four small questions about your skin, hair and habits. We'll build a two-minute
            morning routine made from IMMUSKI essentials — the exact bottles for the exact skin you have today.
          </p>
          {!open && !result && (
            <button
              onClick={() => setOpen(true)}
              data-testid="quiz-start"
              className="mt-8 btn-primary-ink px-8 py-4 text-xs uppercase tracking-[0.28em] inline-flex items-center gap-2"
            >
              Start the quiz <ArrowRight size={14} />
            </button>
          )}
        </div>

        <div className="lg:col-span-7">
          <div className="relative bg-cream-50 border hairline p-8 min-h-[420px] flex flex-col">
            {!open && !result && (
              <div className="m-auto text-center text-ink-500">
                <Sparkles size={28} className="mx-auto text-gold-500" />
                <div className="eyebrow mt-4">Interactive</div>
                <div className="mt-2 font-display text-2xl text-ink-900">Skin & Hair Quiz</div>
                <div className="mt-2 text-xs">4 questions · 60 seconds · Personalised</div>
              </div>
            )}

            {open && !result && (
              <div className="flex-1 flex flex-col" data-testid="quiz-step">
                <div className="flex justify-between items-center">
                  <div className="eyebrow">Question {step + 1} of {QUESTIONS.length}</div>
                  <div className="flex gap-1">
                    {QUESTIONS.map((_, i) => (
                      <span key={i} className={"h-[2px] w-8 " + (i <= step ? "bg-ink-900" : "bg-ink-300")} />
                    ))}
                  </div>
                </div>
                <h3 className="mt-6 font-display text-3xl text-ink-900 leading-tight">{QUESTIONS[step].label}</h3>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {QUESTIONS[step].options.map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => pick(opt)}
                      data-testid={`quiz-opt-${QUESTIONS[step].id}-${opt.v}`}
                      className="text-left border hairline bg-white p-4 hover:bg-cream-200 hover:border-ink-900 transition-colors"
                    >
                      <div className="text-sm text-ink-900">{opt.l}</div>
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} className="mt-6 text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900 self-start">
                    ← Back
                  </button>
                )}
              </div>
            )}

            {result && (
              <div className="flex-1" data-testid="quiz-result">
                <div className="eyebrow text-gold-500">Your recommended routine</div>
                <h3 className="mt-2 font-display text-3xl text-ink-900">A routine made for you.</h3>
                <p className="mt-2 text-sm text-ink-500">Based on your answers, start with these {result.length}:</p>
                <div className="mt-5 space-y-3">
                  {result.map((p, i) => (
                    <Link
                      key={p.id}
                      to={`/product/${p.slug}`}
                      data-testid={`quiz-rec-${p.slug}`}
                      className="flex items-center gap-4 border hairline p-3 bg-white hover:border-ink-900 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-ink-900 text-cream-100 flex items-center justify-center font-display text-sm">
                        {i + 1}
                      </div>
                      <img src={p.hero_image} alt="" className="w-14 h-16 object-cover bg-cream-200" />
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg text-ink-900 truncate">{p.name}</div>
                        <div className="text-xs text-ink-500 truncate">{p.subtitle}</div>
                      </div>
                      <div className="text-sm tabular-nums whitespace-nowrap">₹{p.price.toFixed(0)}</div>
                    </Link>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/shop" className="btn-primary-ink px-6 py-3 text-xs uppercase tracking-[0.28em] inline-flex items-center gap-2" data-testid="quiz-shop-all">
                    <Check size={14} /> Shop the set
                  </Link>
                  <button onClick={restart} className="btn-gold-outline px-6 py-3 text-xs uppercase tracking-[0.28em]" data-testid="quiz-restart">
                    Retake quiz
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
